import { getFiskalOrdersOrderIdDocuments } from '@agos/api-client/fiskal'

import { tt } from './i18n'

/**
 * One fiscal document the register printed for an order, and its reversal if it had one - §9.569,
 * FB-864.
 *
 * **`fiskal_documents` has recorded every print and every storno since §10A.191** with the figure the
 * register was told to print, and nothing could read it back: the *Fiškálne doklady* list reads
 * `orders`, so a row there carries the order's **latest** stamp only. §10A.294 added the per-order
 * read; this is its shape.
 *
 * **Here rather than in either module**, because two screens in two packages render it - the receipts
 * detail (§9.569) and the archive's *História zmien* (§9.570) - and a chronology spelled twice is two
 * chances to order it differently.
 */
export interface FiskalOrderDocument {
  id: number
  kasaId: string | null
  receiptNumber: string | null
  receiptUid: string | null
  virtual: boolean
  totalCents: number | null
  lineCount: number | null
  printedAt: string | null
  stornoAt: string | null
  stornoBy: string | null
}

/**
 * The sequence for one order, oldest first - §10A.294.
 *
 * **The generated client, which is what §9.513's self-clearing list was waiting for.** This was a
 * hand-written `customFetch<FiskalOrderDocument[]>` with the path spelled out, because §10A.294's
 * endpoint post-dated the exported spec and no container could start to export a new one. The path was
 * put on `typed-fetches.test.ts`'s `AWAITING_GENERATED_CLIENT` list with that reason, and on 2026-09-28
 * the spec was refreshed and **that list failed and named this entry** - which is the whole point of it.
 *
 * <p>So the URL now lives in exactly one place and it is generated from the server's own spec. The
 * result is deliberately `unknown`: the generated model is all-optional, as springdoc emits for a
 * record with nullable components, while {@link fiskalDocumentsOf} answers the stricter shape the
 * screens read. **Narrowing at the parser rather than trusting the wire type** is what lets the two
 * differ without a cast.
 */
export function fetchOrderDocuments(orderId: number): Promise<unknown> {
  return getFiskalOrdersOrderIdDocuments(orderId)
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

function count(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

/** What came back, or an empty sequence - a franchise whose documents predate `V59` has none. */
export function fiskalDocumentsOf(payload: unknown): FiskalOrderDocument[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        kasaId: text(row.kasaId),
        receiptNumber: text(row.receiptNumber),
        receiptUid: text(row.receiptUid),
        virtual: row.virtual === true,
        totalCents: count(row.totalCents),
        lineCount: count(row.lineCount),
        printedAt: text(row.printedAt),
        stornoAt: text(row.stornoAt),
        stornoBy: text(row.stornoBy),
      },
    ]
  })
}

/** One thing that happened to a document: it was printed, or it was cancelled. */
export interface FiskalDocumentEvent {
  kind: 'printed' | 'stornoed'
  at: string
  document: FiskalOrderDocument
}

/**
 * The sequence as a chronology - §9.570.
 *
 * **A document is two events, not one.** A receipt printed at 12:00 and voided at 12:30 belongs in a
 * history twice, in two places, or the order of what happened is lost - which is the whole question
 * when a receipt was printed, cancelled and printed again.
 *
 * Sorted by the timestamp rather than by the row, because a reversal is stamped later than its own
 * print but earlier than the re-issue that followed it.
 */
export function fiskalDocumentEvents(
  documents: readonly FiskalOrderDocument[],
): FiskalDocumentEvent[] {
  const events: FiskalDocumentEvent[] = []
  for (const document of documents) {
    if (document.printedAt !== null) {
      events.push({ kind: 'printed', at: document.printedAt, document })
    }
    if (document.stornoAt !== null) {
      events.push({ kind: 'stornoed', at: document.stornoAt, document })
    }
  }
  return events.sort((one, other) => one.at.localeCompare(other.at))
}

/** How many documents an order has had, and how much of that was undone and redone - §9.569. */
export interface FiskalDocumentCounts {
  printed: number
  stornoed: number
  reprinted: number
  live: number
}

/**
 * The owner's ask on the *Fiškálne doklady* screen: *"add there one more thing, how much fiskal were
 * storned and reprinted."*
 *
 * **`reprinted` is a property of the sequence, not of any row.** A document is a re-print when one was
 * printed before it, so the figure is *documents after the first* - which is exactly what a row-level
 * read could never answer and why §10A.294 exists.
 *
 * **`live` is the one that decides money**, because `FiskalClosureService.takingsSince` counts the
 * drawer against orders holding a document that was printed and not cancelled.
 */
export function fiskalDocumentCounts(
  documents: readonly FiskalOrderDocument[],
): FiskalDocumentCounts {
  const printed = documents.filter((document) => document.printedAt !== null).length
  return {
    printed,
    stornoed: documents.filter((document) => document.stornoAt !== null).length,
    reprinted: Math.max(0, printed - 1),
    live: documents.filter(
      (document) => document.printedAt !== null && document.stornoAt === null,
    ).length,
  }
}

/**
 * What the counts say, or `''` when there is nothing unusual to report - §9.569.
 *
 * **Silent on the ordinary receipt**, which is one print and no reversal: a line saying *0 stornoed*
 * on every document is a line nobody reads by the third one. It speaks when a document was undone or
 * redone, which is the case the owner is asking to see.
 */
export function fiskalDocumentCountsNote(counts: FiskalDocumentCounts): string {
  if (counts.stornoed === 0 && counts.reprinted === 0) return ''
  const parts: string[] = []
  if (counts.stornoed > 0) {
    parts.push(`${tt('stornovaných')} ${String(counts.stornoed)}`)
  }
  if (counts.reprinted > 0) {
    parts.push(`${tt('znovu vytlačených')} ${String(counts.reprinted)}`)
  }
  return `${tt('Dokladov')} ${String(counts.printed)} - ${parts.join(', ')}.`
}
